import { LoaderFunction } from 'react-router-dom';
import * as Sentry from '@sentry/react';

// Types
import { AuthContextType } from '../../../../hooks/useAuth';
import { RouteDetailData, RouteHeaderData } from '../types';
import type {
  RouteSegment,
  SurveillanceRoute,
} from '../../../../types/surveillanceRoute';

// GQL
import { client } from '../../../../gql/client';
import {
  GET_SURVEILLANCE_ROUTE_BY_ID,
  GET_ROUTE_SEGMENTS,
} from '../../../../gql/queries/surveillanceRoute';
import { fetchExternalWorksBySegment } from '../../../../gql/surveillanceRouteExternalWorks';
import { GET_ORDERS_BY_IDS_SERVER } from '../../../../gql/queries/orders/orders';

// Header data — resolución de nombres para la cabecera del detalle (Task 4:
// extraído a su propio fichero, ver `headerData.ts`).
import { EMPTY_HEADER_DATA, resolveHeaderData } from './headerData';

// Offline wizard catalogs prefetch
import { prefetchWizardCatalogs } from '../../../../offline/prefetchOfflineData';

// ═══════════════════════════════════════════════════════════════════
// LOADER — caché primero, servidor por detrás (spec 2026-09-11)
//
// 🔴 El problema que resuelve esta forma: con una RAYA de cobertura la app se
// cree online (`navigator.onLine` y Android dicen `true`), así que Apollo NO
// convierte el `network-only` en `cache-first` y las queries no tienen tope de
// espera. La petición ni responde ni falla: se queda colgada. Como el respaldo
// de caché vivía en el `catch`, no llegaba nunca y la pantalla se quedaba en el
// spinner teniendo el recorrido entero cacheado. Sin red del todo sí cargaba —
// de ahí que pareciera que «el offline funciona pero la cobertura mala no».
//
// Ahora el loader devuelve DOS promesas:
//   · `data`  — el snapshot de la caché, que resuelve al instante. Sin caché,
//               es la carga de servidor de siempre (mismo `catch`, mismo
//               «Recorrido no encontrado» cuando no hay nada).
//   · `fresh` — la carga de servidor, SIEMPRE en marcha. Nunca rechaza: si la
//               red falla resuelve `null` y el snapshot se queda en pantalla.
//
// La página pinta `fresh ?? snapshot` (ver `RoutePreviewPage`).
// ═══════════════════════════════════════════════════════════════════

const EMPTY_DETAIL: RouteDetailData = {
  route: null,
  segments: [],
  orderRoid: '',
  headerData: EMPTY_HEADER_DATA,
  externalWorksBySegmentId: {},
};

type CachedQueries = {
  route: SurveillanceRoute;
  segments: RouteSegment[];
};

/**
 * Recorrido y tramos tal cual están en la caché de Apollo, sin tocar la red.
 * `null` si no hay recorrido cacheado — un recorrido sin sus tramos sí vale
 * (los tramos encolados los superpone `usePendingSegments`).
 */
const readCachedQueries = (routeId: string): CachedQueries | null => {
  try {
    const cachedRoute = client.readQuery({
      query: GET_SURVEILLANCE_ROUTE_BY_ID,
      variables: { id: routeId },
    }) as { surveillanceRoute?: SurveillanceRoute } | null;

    if (!cachedRoute?.surveillanceRoute) return null;

    const cachedSegments = client.readQuery({
      query: GET_ROUTE_SEGMENTS,
      variables: { surveillanceRouteId: routeId },
    }) as { routeSegments?: RouteSegment[] } | null;

    return {
      route: cachedRoute.surveillanceRoute,
      segments: cachedSegments?.routeSegments ?? [],
    };
  } catch {
    return null;
  }
};

/**
 * ROID de la Order gemela para la miga de pan.
 *
 * `cache-first` en la carga de servidor; `cache-only` en el snapshot, que no
 * puede salir a la red bajo ningún concepto.
 */
const resolveOrderRoid = async (
  route: SurveillanceRoute | null,
  fetchPolicy: 'cache-first' | 'cache-only'
): Promise<string | string[]> => {
  // Fix (review Task 1): la fragment SOLO trae `orderIDs` (array) —
  // `route.orderId` (singular) nunca lo resuelve GraphQL y quedaba
  // siempre undefined, dejando `orderRoid` en '' permanentemente.
  const orderId = route?.orderIDs?.[0];
  if (!orderId) return '';
  try {
    const orderResponse = await client.query({
      query: GET_ORDERS_BY_IDS_SERVER,
      variables: { ids: [orderId] },
      fetchPolicy,
    });
    const orders = orderResponse.data?.ordersByIds;
    return orders?.length > 0 ? orders[0].roid || '' : '';
  } catch {
    // Order fetch is non-critical — breadcrumb falls back to ID
    return '';
  }
};

/** Cabecera resuelta a nombres; nunca bloquea ni tira abajo la página. */
const resolveHeaderDataSafe = async (
  route: SurveillanceRoute | null,
  fetchPolicy: 'cache-first' | 'cache-only'
): Promise<RouteHeaderData> => {
  if (!route) return EMPTY_HEADER_DATA;
  try {
    return await resolveHeaderData(route, { fetchPolicy });
  } catch {
    // Header enrichment is non-critical — the header card falls
    // back to empty fields, it never blocks/crashes the page.
    return EMPTY_HEADER_DATA;
  }
};

/**
 * La carga de siempre: recorrido y tramos `network-only`, más la cabecera y el
 * ROID resueltos `cache-first`. **Puede rechazar** — quien la consume decide.
 */
const fetchFromServer = async (routeId: string): Promise<RouteDetailData> => {
  const [routeResponse, segmentsResponse, externalWorksBySegmentId] =
    await Promise.all([
    client.query({
      query: GET_SURVEILLANCE_ROUTE_BY_ID,
      variables: { id: routeId },
      fetchPolicy: 'network-only',
    }),
    client.query({
      query: GET_ROUTE_SEGMENTS,
      variables: { surveillanceRouteId: routeId },
      fetchPolicy: 'network-only',
    }),
    // Actuación de cada aviso: consulta aparte, sin caché y que nunca
    // rechaza (sin red o con el back fallando → `null` y la página conserva
    // lo último bueno).
    fetchExternalWorksBySegment(routeId),
  ]);

  // Catálogos del wizard de Actuación (Materiales, Edición, Maquinària…):
  // la básica se crea DESDE AQUÍ y sin cobertura sus pestañas solo tienen
  // lo que haya en caché. Mismo warm-up que el detalle de OT y el de
  // Campaña; fire-and-forget, cada consulta traga su propio error.
  prefetchWizardCatalogs();

  const route = routeResponse.data?.surveillanceRoute || null;

  // orderRoid (breadcrumb) and headerData (cabecera) are independent
  // enrichments of `route` — resolved in parallel, each fault-tolerant
  // on its own so a failure in one never blocks/blanks the other, nor
  // the already-fetched route/segments above.
  const [orderRoid, headerData] = await Promise.all([
    resolveOrderRoid(route, 'cache-first'),
    resolveHeaderDataSafe(route, 'cache-first'),
  ]);

  return {
    route,
    segments: segmentsResponse.data?.routeSegments || [],
    orderRoid,
    headerData,
    externalWorksBySegmentId,
  };
};

export const LoaderRouteDetailed: (
  userData: AuthContextType | null
) => LoaderFunction =
  (userData) =>
  async ({ params }) => {
    const { routeId } = params;
    const enabled = Boolean(routeId && userData?.user);

    // La carga de servidor arranca YA, en paralelo con el snapshot. Es UNA
    // sola petición: las dos ramas de abajo consumen esta misma promesa.
    const serverPromise = enabled
      ? fetchFromServer(routeId as string)
      : Promise.resolve(EMPTY_DETAIL);

    // 🔴 `fresh` NUNCA rechaza. Si rechazara, React Router lo trataría como un
    // error del loader y tumbaría la pantalla justo en el caso que esto viene a
    // resolver. `null` significa «no se pudo refrescar»: manda el snapshot.
    const fresh: Promise<RouteDetailData | null> = serverPromise.catch(() => null);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async (): Promise<RouteDetailData & { error?: unknown }> => {
    if (!enabled) return EMPTY_DETAIL;

    // ── Snapshot ───────────────────────────────────────────────────────────
    // Lo que el operario estaba viendo hace un segundo. Ni una sola consulta
    // sale a la red aquí: todo `cache-only`.
    const cached = readCachedQueries(routeId as string);
    if (cached) {
      const [orderRoid, headerData] = await Promise.all([
        resolveOrderRoid(cached.route, 'cache-only'),
        resolveHeaderDataSafe(cached.route, 'cache-only'),
      ]);
      return {
        route: cached.route,
        segments: cached.segments,
        orderRoid,
        headerData,
        // Sin red en el snapshot: este dato no se guarda (spec § 6).
        externalWorksBySegmentId: {},
      };
    }

    // ── Sin caché: como siempre, se espera al servidor ─────────────────────
    try {
      return await serverPromise;
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error loading route details:', error);
      return { ...EMPTY_DETAIL, error };
    }
  };

    // 🔴 UNA sola clave, como los otros 75 loaders del repo: la carga del
    // servidor viaja DENTRO del snapshot, no como una segunda clave suelta.
    // Un loader con forma propia es el que un tripwire futuro rompe sin que
    // nadie entienda por qué, y la página no gana nada: solo lee `fresh`
    // cuando el `<Await>` ya le ha dado el snapshot.
    return { data: load().then((detail) => ({ ...detail, fresh })) };
  };

export default LoaderRouteDetailed;
